import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { Loader } from '../../components';
import PagedTable from '../../components/sorted-table/PagedTable';
import {
  getMarketPlacePaymentReportSummary,
  getMarketPlaceSaleHistory,
} from '../../api/marketPlacePaymentTransactionOrderDetailApi';
import { centsToDollars } from '../../services/account.service';
import { showToast } from '../../services/toast.service';
import { SaleHistoryItem, SaleHistorySummary, SaleHistoryParams } from '../../types/interfaces/saleHistory.interface';
import { LABEL_CONSTANTS, SALE_HISTORY_CONSTANTS, TABLE_HEADER_CONSTANTS } from '../../constants/common';
import { toastMessages } from '../../constants/errorMessages';
import './saleHistory.scss';

const saleHistoryFetchLimit = 10;

const StatusBadge = ({ status }: { status: string }) => (
  <span className={`tag-badge tag-badge--${status?.toLowerCase().replace(/\s+/g, '_')}`}>{status}</span>
);

const INITIAL_SUMMARY: SaleHistorySummary = {
  totalUnitsSold: 0,
  totalRevenueCents: 0,
  totalOrders: 0,
};

const SaleHistory = ({ accountId }: { accountId: number }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [mobileData, setMobileData] = useState<SaleHistoryItem[]>([]);
  const [summary, setSummary] = useState<SaleHistorySummary>(INITIAL_SUMMARY);
  const [refresh, setRefresh] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const [isMobileView, setIsMobileView] = useState<boolean>(false);

  const hasMountedRef = useRef<boolean>(false);
  const skipNextOffsetEffect = useRef<boolean>(false);
  const isLoadingAdditionalPage: MutableRefObject<boolean> = useRef<boolean>(false);
  const hasAllResults: MutableRefObject<boolean> = useRef<boolean>(false);
  const isLoadingRef = useRef<boolean>(false);
  const callNumber: MutableRefObject<number> = useRef<number>(0);

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth <= 767);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await getMarketPlacePaymentReportSummary(accountId);
      setSummary({
        ...INITIAL_SUMMARY,
        totalUnitsSold: response.data?.unitsSold ?? 0,
        totalRevenueCents: response.data?.netProfitCents ?? 0,
        totalOrders: response.data?.totalOrders ?? 0,
      });
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  }, [accountId]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const loadData = useCallback(
    async (currentOffset: number) => {
      if (hasAllResults.current || isLoadingAdditionalPage.current || isLoadingRef.current) return;
      callNumber.current++;
      const callNum = callNumber.current;

      try {
        isLoadingRef.current = true;
        setLoading(true);
        const response = await getMarketPlaceSaleHistory(accountId, {
          limit: saleHistoryFetchLimit,
          offset: currentOffset,
        });
        if (callNum !== callNumber.current) return;
        const data = response.data as SaleHistoryItem[];
        hasAllResults.current = data.length < saleHistoryFetchLimit;
        let nextData: SaleHistoryItem[] = [];
        setMobileData(prev => {
          const combined = currentOffset === 0 ? data : [...prev, ...data];
          const map = new Map<number, SaleHistoryItem>();
          combined.forEach(item => {
            map.set(item.specimenId, item);
          });
          nextData = Array.from(map.values());
          return nextData;
        });
      } catch {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
        isLoadingAdditionalPage.current = false;
      }
    },
    [accountId],
  );

  useEffect(() => {
    if (isMobileView && !hasMountedRef.current) {
      hasMountedRef.current = true;
      loadData(0);
    }
  }, [isMobileView, loadData]);

  useEffect(() => {
    if (skipNextOffsetEffect.current) {
      skipNextOffsetEffect.current = false;
      return;
    }
    if (isMobileView && offset > 0) {
      loadData(offset);
    }
  }, [isMobileView, offset, loadData]);

  const handleScroll = useCallback(() => {
    if (hasAllResults.current || isLoadingAdditionalPage.current || isLoadingRef.current) return;
    const scrollTop = window.scrollY;
    const clientHeight = window.innerHeight;
    const scrollHeight = document.documentElement.scrollHeight;
    if (scrollTop + clientHeight >= scrollHeight - 150) {
      isLoadingAdditionalPage.current = true;
      setOffset(prev => prev + saleHistoryFetchLimit);
    }
  }, []);

  useEffect(() => {
    globalThis.addEventListener('scroll', handleScroll);
    globalThis.addEventListener('touchmove', handleScroll);
    return () => {
      globalThis.removeEventListener('scroll', handleScroll);
      globalThis.removeEventListener('touchmove', handleScroll);
    };
  }, [handleScroll]);

  const getSaleHistoryData = async (params: SaleHistoryParams) => {
    try {
      const response = await getMarketPlaceSaleHistory(accountId, {
        limit: params?.limit,
        offset: params?.offset,
      });
      return response;
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      return { data: [] };
    }
  };

  const footerElement = (
    <div className="footer">
      <span className="footer-item">
        {SALE_HISTORY_CONSTANTS.TOTAL_UNITS_SOLD}: <strong>{summary.totalUnitsSold}</strong>
      </span>
      <span className="footer-sep" />
      <span className="footer-item">
        {SALE_HISTORY_CONSTANTS.TOTAL_REVENUE}: <strong>${centsToDollars(summary.totalRevenueCents)}</strong>
      </span>
      <span className="footer-sep" />
      <span className="footer-item">
        {SALE_HISTORY_CONSTANTS.TOTAL_ORDERS}: <strong>{summary.totalOrders}</strong>
      </span>
    </div>
  );

  return (
    <div className="sale-history">
      <div className="sale-history-table">
        <div className="content">
          <PagedTable
            headers={[
              { displayName: TABLE_HEADER_CONSTANTS.PRODUCT_NAME },
              { displayName: TABLE_HEADER_CONSTANTS.AVAILABILITY_STATUS },
              { displayName: TABLE_HEADER_CONSTANTS.CATEGORY },
              { displayName: TABLE_HEADER_CONSTANTS.BREED },
              { displayName: TABLE_HEADER_CONSTANTS.TOTAL_LISTED },
              { displayName: TABLE_HEADER_CONSTANTS.UNITS_SOLD },
              { displayName: TABLE_HEADER_CONSTANTS.REMAINING },
              { displayName: TABLE_HEADER_CONSTANTS.ORDERS },
              { displayName: TABLE_HEADER_CONSTANTS.REVENUE },
            ]}
            buildRow={(item: SaleHistoryItem) => [
              <div key={`product-name-${item.specimenId}`} className="product-name-wrap">
                <span className="product-name">{item.animal?.name}</span>
                <span className="product-code">{item.animal?.code}</span>
                <span className="product-sold-percent">
                  {item.soldPercentage}
                  {SALE_HISTORY_CONSTANTS.PERCENT_SOLD}
                </span>
              </div>,
              <StatusBadge key={`status-${item.specimenId}`} status={item.availabilityStatus} />,
              item.specimenType?.name,
              item.breed?.name,
              item.totalListedUnits,
              item.soldUnits,
              item.remainingUnits,
              item.totalOrders,
              `$${centsToDollars(item.revenueAmountInCents)}`,
            ]}
            getData={getSaleHistoryData}
            pageSize={saleHistoryFetchLimit}
            height={520}
            refresh={refresh}
            setRefresh={setRefresh}
            noResultsMessage={LABEL_CONSTANTS.NO_SALE_HISTORY_FOUND}
            footerElement={footerElement}
          />
        </div>

        <div className="xs-content">
          {!loading && mobileData.length === 0 ? (
            <label className="no-sale-history-found">{LABEL_CONSTANTS.NO_SALE_HISTORY_FOUND}</label>
          ) : (
            <>
              {mobileData.map((item: SaleHistoryItem, index: number) => (
                <div className="card max-width" key={`sale-history-${item.specimenId}-${index}`}>
                  <div className="sale-history-card">
                    <div className="sale-history-content">
                      <div className="sale-history-info">
                        <div className="sale-history-item">
                          <label>{`${TABLE_HEADER_CONSTANTS.PRODUCT_NAME}: `}</label>
                          <label>{item.animal?.name}</label>
                          <label>{item.animal?.code}</label>
                          <span className="product-sold-percent">
                            {item.soldPercentage}
                            {SALE_HISTORY_CONSTANTS.PERCENT_SOLD}
                          </span>
                        </div>
                        <div className="sale-history-item">
                          <label>{`${TABLE_HEADER_CONSTANTS.CATEGORY}: `}</label>
                          <label>{item.specimenType?.name}</label>
                        </div>
                        <div className="sale-history-item">
                          <label>{`${TABLE_HEADER_CONSTANTS.AVAILABILITY_STATUS}: `}</label>
                          <StatusBadge status={item.availabilityStatus} />
                        </div>
                      </div>
                      <div className="sale-history-info">
                        <div className="sale-history-item">
                          <label>{`${TABLE_HEADER_CONSTANTS.BREED}: `}</label>
                          <label>{item.breed?.name}</label>
                        </div>
                        <div className="sale-history-item">
                          <label>{`${TABLE_HEADER_CONSTANTS.TOTAL_LISTED}: `}</label>
                          <label>{item.totalListedUnits}</label>
                        </div>
                        <div className="sale-history-item">
                          <label>{`${TABLE_HEADER_CONSTANTS.UNITS_SOLD}: `}</label>
                          <label>{item.soldUnits}</label>
                        </div>
                      </div>
                      <div className="sale-history-info">
                        <div className="sale-history-item">
                          <label>{`${TABLE_HEADER_CONSTANTS.REMAINING}: `}</label>
                          <label>{item.remainingUnits}</label>
                        </div>
                        <div className="sale-history-item">
                          <label>{`${TABLE_HEADER_CONSTANTS.ORDERS}: `}</label>
                          <label>{item.totalOrders}</label>
                        </div>
                        <div className="sale-history-item">
                          <label>{`${TABLE_HEADER_CONSTANTS.REVENUE}: `}</label>
                          <label>{`$${centsToDollars(item.revenueAmountInCents)}`}</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {loading && mobileData.length > 0 && <Loader addedSpace loaderSize="small" />}
            </>
          )}
        </div>

        {!loading && mobileData.length > 0 && (
          <div className="xs-footer">
            <div className="card max-width">
              <div className="sale-history-card">
                <div className="sale-history-content">
                  <div className="sale-history-info">
                    <div className="sale-history-item">
                      <label>{SALE_HISTORY_CONSTANTS.TOTAL_UNITS_SOLD}</label>
                      <label>
                        <strong>{summary.totalUnitsSold}</strong>
                      </label>
                    </div>
                    <div className="sale-history-item">
                      <label>{SALE_HISTORY_CONSTANTS.TOTAL_REVENUE}</label>
                      <label>
                        <strong>${centsToDollars(summary.totalRevenueCents)}</strong>
                      </label>
                    </div>
                    <div className="sale-history-item">
                      <label>{SALE_HISTORY_CONSTANTS.TOTAL_ORDERS}</label>
                      <label>
                        <strong>{summary.totalOrders}</strong>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SaleHistory;
